import { Location, useLocation, useNavigate } from "react-router-dom";
import { useCallback } from "react";
import { getPreviousRelativeUrlFromLocation } from "../query/location";
import {
  BusinessesLoginDocument,
  BusinessesLoginQuery,
  BusinessesLoginQueryVariables,
} from "../../generated/graphql";
import { getProjectUrl } from "../../features/project/projectHooks";
import getApolloClient from "../query/getApolloClient";
import getErrorMessage from "../query/getErrorMessage";
import { personalizePath } from "../constants";

export default function useLoginRedirectQuery(
  to?: string
): () => Promise<void> {
  const location = useLocation();

  const navigate = useNavigate();

  const redirect = useCallback(async () => {
    const data = await getBusinessesLoginQuery();

    if (!data) {
      return Promise.resolve();
    }

    const redirectTo = to ?? getTo(location, data);

    console.log("[useLoginRedirectQuery] redirecting to", redirectTo);

    return navigate(redirectTo, { state: location.state });
  }, [location, navigate, to]);

  return redirect;
}

async function getBusinessesLoginQuery(): Promise<BusinessesLoginQuery | null> {
  try {
    const { data } = await getApolloClient().query<
      BusinessesLoginQuery,
      BusinessesLoginQueryVariables
    >({ query: BusinessesLoginDocument, fetchPolicy: "network-only" });
    return data;
  } catch (error) {
    console.error("[getBusinessesLoginQuery] error", {
      message: getErrorMessage(error),
      error,
    });
    return null;
  }
}

function getTo(location: Location, data: BusinessesLoginQuery): string {
  const previousRelativeUrl = getPreviousRelativeUrlFromLocation(location);

  if (previousRelativeUrl && previousRelativeUrl !== "/") {
    return previousRelativeUrl;
  }

  if (
    location.pathname !== personalizePath &&
    Object.keys(JSON.parse(data.me.metadataJson) ?? {}).length === 0
  ) {
    return personalizePath;
  }

  if (!data.primaryBusiness) {
    return "/";
  }

  const { projects } = data.primaryBusiness.business;

  if (projects.length === 1) {
    return getProjectUrl({ projectId: projects[0].id, selectedView: "logic" });
  }

  return "/";
}
